/**
 * dates.js — Payday projection and billing cycle utilities
 *
 * Core date logic for the spendable balance algorithm:
 * - Projects next payday from a biweekly anchor date
 * - Determines which bills fall in the current pay period
 * - Calculates days remaining until next payday
 *
 * Payday schedule: Biweekly (every 14 days) on Wednesdays.
 * Anchor date: 2026-02-25 (a known Wednesday payday).
 */

/**
 * Calculate the next payday from a biweekly anchor.
 * Projects forward from the anchor in 14-day increments until we find
 * the next payday that is >= today.
 *
 * @param {string} anchor - ISO date string of a known payday (e.g., "2026-02-25")
 * @param {Date} [today] - Reference date (defaults to now, useful for testing)
 * @returns {Date} Next upcoming payday (midnight local time)
 */
export function getNextPayday(anchor, today = new Date()) {
  const anchorDate = new Date(anchor + 'T00:00:00');
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // If anchor is in the future, walk backwards first
  let payday = new Date(anchorDate);

  if (payday > todayMidnight) {
    // Walk backwards to find the most recent past payday
    while (payday > todayMidnight) {
      payday.setDate(payday.getDate() - 14);
    }
    // Now walk forward to the next one
    payday.setDate(payday.getDate() + 14);
  } else {
    // Walk forward until payday >= today
    while (payday < todayMidnight) {
      payday.setDate(payday.getDate() + 14);
    }
  }

  // If payday IS today, that's today's payday — the "next" is 14 days out
  // Actually, if payday is today, we consider it the current payday (money is here)
  // The user wants to see "days until next payday" so if today IS payday, next = +14
  if (payday.getTime() === todayMidnight.getTime()) {
    // Today is payday — return the NEXT one (14 days from now)
    payday.setDate(payday.getDate() + 14);
  }

  return payday;
}

/**
 * Calculate days remaining until next payday.
 * @param {string} anchor - ISO date string of a known payday
 * @param {Date} [today] - Reference date
 * @returns {number} Number of days until next payday (1-14)
 */
export function getDaysUntilPayday(anchor, today = new Date()) {
  const nextPayday = getNextPayday(anchor, today);
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((nextPayday - todayMidnight) / (1000 * 60 * 60 * 24));
}

/**
 * Get the current pay period boundaries.
 * Returns the start (last payday, inclusive) and end (next payday, exclusive).
 *
 * @param {string} anchor - ISO date string of a known payday
 * @param {Date} [today] - Reference date
 * @returns {{ start: Date, end: Date }} Pay period boundaries
 */
export function getCurrentPayPeriod(anchor, today = new Date()) {
  const nextPayday = getNextPayday(anchor, today);
  const start = new Date(nextPayday);
  start.setDate(start.getDate() - 14);
  return { start, end: nextPayday };
}

/**
 * Check if a bill's due date falls between today and the next payday.
 * Used to determine which unpaid bills should be subtracted from spendable balance.
 *
 * Bills are identified by due_day (1-31, day of month).
 * A bill is "upcoming" if its due date in the current month falls within
 * [today, nextPayday).
 *
 * Edge case: If the pay period spans a month boundary (e.g., Mar 25 → Apr 8),
 * we check both months.
 *
 * @param {number} dueDay - Day of month the bill is due (1-31)
 * @param {string} anchor - ISO date string of a known payday
 * @param {Date} [today] - Reference date
 * @returns {boolean} True if bill is due before next payday
 */
export function isBillDueBeforePayday(dueDay, anchor, today = new Date()) {
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const nextPayday = getNextPayday(anchor, today);

  // Generate candidate due dates in current month and next month
  const candidates = [];

  // Current month
  const currentMonthDue = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth(), dueDay);
  candidates.push(currentMonthDue);

  // Next month (handles pay periods spanning month boundaries)
  const nextMonthDue = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth() + 1, dueDay);
  candidates.push(nextMonthDue);

  // Count a bill if it was due this month (even if already past) OR is due before next payday.
  // Past-due unpaid bills still need to be paid and should reduce spendable.
  return candidates.some(d => d >= startOfMonth && d < nextPayday);
}

/**
 * Get the specific due date(s) for a bill that fall within the current pay period.
 * Returns actual Date objects for display purposes.
 *
 * @param {number} dueDay - Day of month the bill is due
 * @param {string} anchor - ISO date string of a known payday
 * @param {Date} [today] - Reference date
 * @returns {Date[]} Array of due dates within the pay period (usually 0 or 1)
 */
export function getBillDueDatesInPeriod(dueDay, anchor, today = new Date()) {
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const nextPayday = getNextPayday(anchor, today);
  const dates = [];

  const currentMonthDue = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth(), dueDay);
  if (currentMonthDue >= todayMidnight && currentMonthDue < nextPayday) {
    dates.push(currentMonthDue);
  }

  const nextMonthDue = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth() + 1, dueDay);
  if (nextMonthDue >= todayMidnight && nextMonthDue < nextPayday) {
    dates.push(nextMonthDue);
  }

  return dates;
}

/**
 * Get the last business day (Mon–Fri) of a given month.
 * If the last day of the month is Saturday, returns Friday.
 * If Sunday, returns the Friday before.
 * Used for VA disability and similar end-of-month government payments.
 *
 * @param {number} year
 * @param {number} month - 0-indexed (0 = January)
 * @returns {Date} Last business day of the month (midnight local time)
 */
export function getLastBusinessDay(year, month) {
  // Last calendar day of the month
  const last = new Date(year, month + 1, 0);
  const dow = last.getDay(); // 0=Sun, 6=Sat
  if (dow === 6) last.setDate(last.getDate() - 1); // Saturday → Friday
  if (dow === 0) last.setDate(last.getDate() - 2); // Sunday → Friday
  return last;
}

/**
 * Get urgency level for an upcoming bill based on days until due.
 * @param {number} dueDay - Day of month
 * @param {Date} [today] - Reference date
 * @returns {'urgent'|'soon'|'normal'} Urgency classification
 */
export function getBillUrgency(dueDay, today = new Date()) {
  const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  // Try current month first
  let dueDate = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth(), dueDay);
  if (dueDate < todayMidnight) {
    // Already passed this month, check next month
    dueDate = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth() + 1, dueDay);
  }

  const daysUntil = Math.round((dueDate - todayMidnight) / (1000 * 60 * 60 * 24));

  if (daysUntil <= 3) return 'urgent';
  if (daysUntil <= 7) return 'soon';
  return 'normal';
}
